import { useContext, useEffect, useMemo, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Loader,
  NotificationType,
  Tab,
  TabHeader,
} from "@transfr-inc/dashboard-components";
import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
  Textarea,
} from "@transfr-inc/dashboard-components/forms";
import {
  DateRangePreset,
  DateRangeSelector,
  MetricPickerModal,
  OutcomeTiles,
  OutcomesEmptyState,
  OutcomesEmptyStateKind,
  ALL_TIME_START,
  defaultPickerDraft,
  metricById,
  resolveDateFilter,
  resolveDateRange,
  useMetricRegistry,
} from "@transfr-inc/dashboard-components/outcome-metrics";
import { Product } from "@transfr-inc/dashboard-components/constants";

import { ToggleField } from "@components/forms";
import { MULTI_ORG_PARENT } from "@constants/organization-types";
import { SessionContext } from "@context/session.context";
import { useNotification } from "@hooks/use-notification";
import { ApplicationId } from "@lib/constants";
import {
  ConfigurationKeys,
  ConfigurationRequestKeys,
} from "@services/configuration";

import { DeckHistoryRow } from "./impact-report.deck-history";
import { ImpactReportDatePickerButton } from "./impact-report.date-picker";
import { ImpactReportPromptConfigModal } from "./impact-report.prompt-config.modal";
import { ImpactReportMetricRegistry } from "./impact-report.metric-registry";
import { services } from "@src/dependencies";
import { useApiRequest } from "@utils/http-client";
import { parseStringBoolean } from "@utils/utils";

import "./impact-report.tab.scss";

// v1 ships a single report type; the selector scopes the form for when more
// report types exist.
const REPORT_TYPES = [{ id: "standard", name: "Impact Report" }];
const DEFAULT_REPORT_TYPE = REPORT_TYPES[0];

// The saved inputs pack carries exactly this many headline metrics: the top
// of the picker's ordered selection become the headline outcome tiles.
const HEADLINE_METRIC_COUNT = 4;

// The internal portal has no per-org licensing wired into this tab yet, and
// the metric registry's role gating has no CS roles — stub the full catalog
// (org-admin view, every product line) until real values flow in.
const ALL_PRODUCTS = [
  Product.TS,
  Product.CE,
  Product.TRK,
  Product.PROG,
  Product.BASICS,
];

// Mirrors the inputs schema's looking_ahead max_length on the backend.
const LOOKING_AHEAD_MAX_LENGTH = 5000;
// Mirrors the inputs schema's use_case_context max_length on the backend.
const USE_CASE_CONTEXT_MAX_LENGTH = 5000;

const GOAL_PLACEHOLDERS = [
  "e.g. Expand career exposure — give every 9th–10th grader at least 3 hands-on career simulations before course selection.",
  "e.g. Raise CTE completion — lift program completion rate 10 points year-over-year across enrolled learners.",
  "e.g. Grow credential attainment — 200 learners earn an industry-recognized credential this academic year.",
];

/**
 * One metric's tile state read off the summary request: in flight reads as
 * loading, a failed request as an error, and a metric the response omits as
 * "no data" — never a 0 the backend didn't send. Mirrors unified-dashboard's
 * consumption contract for the outcomes proxy.
 */
function tileFromSummary({ loading, error, response }, metricId) {
  if (loading) {
    return { isLoading: true };
  }
  if (error) {
    return { isError: true };
  }
  const entry = response?.metrics?.[metricId];
  return entry ? { value: entry.value } : { isEmpty: true };
}

/** The concrete { start, end } range the current date filter covers. */
function resolveRange(dateFilter) {
  if (dateFilter?.start && dateFilter?.end) {
    return { start: dateFilter.start, end: dateFilter.end };
  }
  return resolveDateRange(dateFilter?.preset);
}

/**
 * "All Time" starts where this account's data does, not at the platform epoch
 * (ENG-24536). `resolveDateFilter` resolves the preset to ALL_TIME_START (Jan
 * 2019), so an applied All Time filter is floored here. The preset is kept:
 * the selector is given the same `allTimeStart`, so reopening it highlights
 * All Time and shows the floored dates rather than the epoch.
 */
function floorAllTime(dateFilter, allTimeStart) {
  if (dateFilter?.preset !== DateRangePreset.ALL_TIME) {
    return dateFilter;
  }
  const { start, end } = resolveRange(dateFilter);
  return start >= allTimeStart
    ? dateFilter
    : { ...dateFilter, start: allTimeStart, end };
}

const ISO_DATE_HEAD = /^\d{4}-\d{2}-\d{2}/;

/**
 * The calendar date an ISO 8601 value names, as "YYYY-MM-DD". Read off the
 * string rather than through a Date, which would re-interpret a bare date as
 * UTC midnight and can shift the day (unified-dashboard's useAllTimeStart).
 */
function isoCalendarDate(value) {
  return typeof value === "string" && ISO_DATE_HEAD.test(value)
    ? value.slice(0, 10)
    : undefined;
}

// Window math runs entirely in UTC: local-time millisecond arithmetic is not
// DST-invariant, and a derived date that drifts across midnight would differ
// from the backend's calendar-day math — whose pack validator then rejects
// the save with a length-mismatch 400.
const displayComparisonDate = (value) =>
  value.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

const parseDateUtc = (value) => new Date(`${value}T00:00:00Z`);

const toDateString = (value) =>
  `${value.getUTCFullYear()}-${String(value.getUTCMonth() + 1).padStart(2, "0")}-${String(
    value.getUTCDate(),
  ).padStart(2, "0")}`;

// react-datepicker (the comparison-start control) works in local-time Dates;
// the window math above is UTC. Convert by calendar components at the
// boundary so the date can't shift a day crossing timezones.
const utcToLocalDate = (value) =>
  new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());

const localDateToString = (value) =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(
    value.getDate(),
  ).padStart(2, "0")}`;

/**
 * The comparison window as { start, end } UTC-midnight Dates: the AM's chosen
 * start (end derived so the window is length-locked to the reporting range),
 * else the immediately preceding equivalent window. Mirrors
 * _comparison_date_range in core-platform, which owns the real math; the
 * length lock is enforced by the backend pack model.
 */
function comparisonWindow(dateFilter, chosenStart) {
  const { start, end } = resolveRange(dateFilter) ?? {};
  if (!start || !end) {
    return;
  }
  const dayMs = 24 * 60 * 60 * 1000;
  const startMs = parseDateUtc(start).getTime();
  const lengthMs = parseDateUtc(end).getTime() - startMs;
  const comparisonStartMs = chosenStart
    ? parseDateUtc(chosenStart).getTime()
    : startMs - dayMs - lengthMs;
  return {
    start: new Date(comparisonStartMs),
    end: new Date(comparisonStartMs + lengthMs),
  };
}

/**
 * Preview % change for one metric — same rules as the backend's
 * _metric_change_display: no comparable history (null) or a zero baseline
 * renders no value, never a misleading figure.
 */
function formatMetricChange(current, previous) {
  if (previous == undefined || previous === 0) {
    return;
  }
  const pct = (((current ?? 0) - previous) / previous) * 100;
  return `${pct >= 0 ? "+" : ""}${Math.round(pct)}%`;
}

export function ImpactReportBuilder({ organization, childOrgs }) {
  const registry = useMetricRegistry();
  const { impactReportService, configurationService } = services;
  const { addNotification } = useNotification();
  const { role } = useContext(SessionContext);
  const orgCode = organization.code;

  // Dev tooling gate: the prompt-config button only renders when the global
  // config-service flag is on. Read from the app-wide params (org-independent).
  const [promptConfigEnabled, setPromptConfigEnabled] = useState(false);
  const [promptConfigOpen, setPromptConfigOpen] = useState(false);
  useEffect(() => {
    configurationService
      .getConfigurations(ApplicationId.DASHBOARD, role)
      .then((response) => {
        const settings =
          response?.[ConfigurationRequestKeys.CONFIG_SETTINGS] ?? response;
        const param = settings?.find(
          (config) =>
            config.name ===
            ConfigurationKeys.IMPACT_REPORT_PROMPT_CONFIG_ENABLED,
        );
        setPromptConfigEnabled(parseStringBoolean(param?.value));
      })
      .catch((error) => {
        console.error(
          "[Impact Report] - Failed to load prompt config flag",
          error,
        );
        setPromptConfigEnabled(false);
      });
  }, []);

  const [reportType, setReportType] = useState(DEFAULT_REPORT_TYPE);

  // The summary API takes a flat list of org codes — no server-side hierarchy
  // expansion. A multi-org parent is an umbrella: its usage lives in the child
  // orgs, so its scope is the child codes alone and its own code is never
  // sent (unified-dashboard's multi-org convention). A parent with no
  // children has an empty scope — no request, tiles show "no data".
  const isParentOrg = organization.typeId === MULTI_ORG_PARENT.id;
  const scopeOrgs = useMemo(
    () => (isParentOrg ? (childOrgs ?? []) : [organization]),
    [isParentOrg, organization, childOrgs],
  );

  // Where "All Time" starts for this account (ENG-24536): the org's licensing
  // (`issued`) date — the anchor the deck's partnership-start line and the
  // dashboard's All Time preset (ENG-23816) already use. A parent's data lives
  // in its children, so the earliest child date floors the parent. Never
  // earlier than the platform epoch, which also covers a missing date.
  const allTimeStart = useMemo(() => {
    let earliest;
    for (const org of scopeOrgs) {
      const issued = isoCalendarDate(org?.issued);
      if (issued && (!earliest || issued < earliest)) {
        earliest = issued;
      }
    }
    return earliest && earliest > ALL_TIME_START ? earliest : ALL_TIME_START;
  }, [scopeOrgs]);

  // The filter always carries concrete start/end dates (resolved locally from
  // a chosen preset) so the selector's trigger can show the covered range —
  // there is no server round-trip resolving presets on this tab.
  const [dateFilter, setDateFilter] = useState(() =>
    resolveDateFilter({ preset: DateRangePreset.ACADEMIC_YEAR }),
  );
  // Exactly HEADLINE_METRIC_COUNT checked from the start, so the capped picker
  // never opens over its limit.
  const [draft, setDraft] = useState(() => {
    const base = defaultPickerDraft(registry, {
      licensedProducts: ALL_PRODUCTS,
    });
    return {
      ...base,
      checkedIds: base.checkedIds.slice(0, HEADLINE_METRIC_COUNT),
    };
  });
  const [goals, setGoals] = useState(() => GOAL_PLACEHOLDERS.map(() => ""));
  const [lookingAhead, setLookingAhead] = useState("");
  const [useCaseContext, setUseCaseContext] = useState(""); // ENG-24878
  const [includeComparison, setIncludeComparison] = useState(false); // off by default (ENG-23679)
  // "YYYY-MM-DD" start of an explicit comparison window (end is derived,
  // length-locked to the reporting range); null = previous equivalent period.
  const [comparisonStart, setComparisonStart] = useState();
  const [pickerOpen, setPickerOpen] = useState(false);

  const { loading, response } = useApiRequest(() =>
    impactReportService.getInputs(orgCode, reportType.id),
  );

  const { loading: saving, sendRequest: sendSaveRequest } = useApiRequest(
    (pack) => impactReportService.saveInputs(orgCode, reportType.id, pack),
    false,
  );

  // Local state rather than useApiRequest: the notification needs the API
  // error detail at call time, which the hook swallows into render state.
  const [generating, setGenerating] = useState(false);

  const { response: decksResponse, sendRequest: sendDecksRequest } =
    useApiRequest(() => impactReportService.getDecks(orgCode));
  const decks = decksResponse?.decks ?? [];

  // Seed the form from the saved pack; a GET with `inputs: null` (nothing
  // saved yet) leaves the defaults in place.
  useEffect(() => {
    const inputs = response?.inputs;
    if (!inputs) {
      return;
    }
    setDateFilter(inputs.dateRange);
    setDraft((current) => ({
      checkedIds: inputs.metrics,
      // Keep the full catalog order, with the saved headline metrics first.
      orderedIds: [
        ...inputs.metrics,
        ...current.orderedIds.filter((id) => !inputs.metrics.includes(id)),
      ],
    }));
    setGoals(GOAL_PLACEHOLDERS.map((_, index) => inputs.goals?.[index] ?? ""));
    setLookingAhead(inputs.lookingAhead ?? "");
    setUseCaseContext(inputs.useCaseContext ?? "");
    setIncludeComparison(inputs.includeComparison ?? false);
    setComparisonStart(inputs.comparisonDateRange?.start ?? undefined);
  }, [response]);

  // The headline metrics: the picker's ordered selection, capped at the count
  // the report (and the saved pack) carries.
  const headlineIds = useMemo(() => {
    const checked = new Set(draft.checkedIds);
    return draft.orderedIds
      .filter((id) => checked.has(id))
      .slice(0, HEADLINE_METRIC_COUNT);
  }, [draft]);

  const headlineMetrics = useMemo(
    () => headlineIds.map((id) => metricById(registry, id)).filter(Boolean),
    [registry, headlineIds],
  );

  const { start: rangeStart, end: rangeEnd } = resolveRange(dateFilter);

  const summaryOrgCodes = useMemo(
    () => scopeOrgs.map((org) => org.code),
    [scopeOrgs],
  );

  const {
    loading: summaryLoading,
    response: summaryResponse,
    error: summaryError,
    sendRequest: sendSummaryRequest,
  } = useApiRequest(
    () =>
      impactReportService.getDashboardSummary(summaryOrgCodes, {
        startDate: rangeStart,
        endDate: rangeEnd,
        metrics: headlineIds,
      }),
    false,
  );

  // Live values re-pull whenever the org, period, or headline selection
  // changes; only the tile data refreshes — selection and goals are kept.
  const headlineIdsKey = headlineIds.join(",");
  const summaryOrgCodesKey = summaryOrgCodes.join(",");
  useEffect(() => {
    if (summaryOrgCodes.length === 0) {
      return;
    }
    sendSummaryRequest();
  }, [summaryOrgCodesKey, rangeStart, rangeEnd, headlineIdsKey]);

  // A 403 means the viewer lacks outcomes access to this org: the whole tile
  // row gives way to the access-denied state (see the render below). Any other
  // failure surfaces per tile.
  const accessDenied = summaryError?.status === 403;

  const tileData = useMemo(
    () =>
      Object.fromEntries(
        headlineIds.map((id) => [
          id,
          tileFromSummary(
            {
              loading: summaryLoading,
              error: summaryError,
              response: summaryResponse,
            },
            id,
          ),
        ]),
      ),
    [headlineIds, summaryLoading, summaryError, summaryResponse],
  );

  // Comparison-period values for the preview chips (ENG-23679) — same
  // summary endpoint, comparison window. Only fetched while the toggle is on.
  const comparisonRange = useMemo(
    () => comparisonWindow(dateFilter, comparisonStart),
    [dateFilter, comparisonStart],
  );
  const comparisonStartKey = comparisonRange
    ? toDateString(comparisonRange.start)
    : "";
  const comparisonEndKey = comparisonRange
    ? toDateString(comparisonRange.end)
    : "";
  // Latest permissible start: the previous-equivalent window's own start —
  // any later and the derived end would overlap the reporting period.
  const defaultComparisonWindow = useMemo(
    () => comparisonWindow(dateFilter),
    [dateFilter],
  );
  const latestComparisonStart = defaultComparisonWindow
    ? toDateString(defaultComparisonWindow.start)
    : undefined;

  // No comparison window can start before the account had data (ENG-24538):
  // the picker floors at `allTimeStart`, and when even the latest permissible
  // start falls before it — the reporting period already reaches back to the
  // floor — there is no history to compare against, so the toggle is disabled.
  const comparisonAvailable =
    !latestComparisonStart || latestComparisonStart >= allTimeStart;

  useEffect(() => {
    if (!comparisonAvailable && includeComparison) {
      setIncludeComparison(false);
    }
  }, [comparisonAvailable, includeComparison]);

  // The picker's min/max only constrain a fresh pick: an explicit start already
  // in state survives a reporting-period (or floor) change, leaving a derived
  // window that overlaps — or trails — the new reporting period, or predates
  // the account's data. Reset such a start to the computed default instead of
  // persisting an invalid window into the pack.
  useEffect(() => {
    if (
      comparisonStart &&
      ((latestComparisonStart && comparisonStart > latestComparisonStart) ||
        comparisonStart < allTimeStart)
    ) {
      setComparisonStart(undefined);
    }
  }, [comparisonStart, latestComparisonStart, allTimeStart]);

  // Belt to the effects' braces: the resets land a commit after the state they
  // correct, so Save/Generate must not trust an out-of-range window in the
  // intervening render.
  const comparisonWindowValid =
    !includeComparison ||
    (comparisonAvailable &&
      (!comparisonStart ||
        ((!latestComparisonStart || comparisonStart <= latestComparisonStart) &&
          comparisonStart >= allTimeStart)));
  const {
    response: comparisonResponse,
    sendRequest: sendComparisonRequest,
    loading: comparisonLoading,
    error: comparisonError,
  } = useApiRequest(
    () =>
      impactReportService.getDashboardSummary(summaryOrgCodes, {
        startDate: comparisonStartKey,
        endDate: comparisonEndKey,
        metrics: headlineIds,
      }),
    false,
  );

  useEffect(() => {
    if (comparisonError) {
      console.error(
        "[Impact Report] - Failed to load comparison summary",
        comparisonError,
      );
    }
  }, [comparisonError]);

  // The window/selection the in-flight (or settled) comparison fetch was
  // issued for. `useApiRequest(fn, false)` starts with `loading: false`, so
  // loading flags alone can't distinguish "not yet requested" (toggle just
  // flipped on) or "answered for a previous window" from a settled fetch —
  // both would paint a chip value that asserts unfetched facts.
  const comparisonKey = [
    summaryOrgCodesKey,
    comparisonStartKey,
    comparisonEndKey,
    headlineIdsKey,
  ].join("|");
  const [requestedComparisonKey, setRequestedComparisonKey] = useState();

  useEffect(() => {
    if (
      !includeComparison ||
      summaryOrgCodes.length === 0 ||
      !comparisonRange
    ) {
      return;
    }
    setRequestedComparisonKey(comparisonKey);
    sendComparisonRequest();
  }, [
    includeComparison,
    summaryOrgCodesKey,
    comparisonStartKey,
    comparisonEndKey,
    headlineIdsKey,
  ]);

  // Pending until the current window's fetch has actually settled: in flight,
  // not yet issued for this window, or issued but neither response nor error
  // back yet. Chips render a loader in this state — never "no history" or a
  // previous window's percentage.
  const previewPending =
    summaryLoading ||
    comparisonLoading ||
    requestedComparisonKey !== comparisonKey ||
    (!comparisonResponse && !comparisonError);

  // Per-metric preview change, keyed by metric id; undefined = no history.
  // Gated on the pending state (like tileData gates on summaryLoading) so a
  // fresh comparison never renders against a stale reporting period, or vice
  // versa.
  const previewChanges = useMemo(() => {
    if (!includeComparison || previewPending) {
      return {};
    }
    return Object.fromEntries(
      headlineIds.map((id) => [
        id,
        formatMetricChange(
          summaryResponse?.metrics?.[id]?.value,
          comparisonResponse?.metrics?.[id]?.value,
        ),
      ]),
    );
  }, [
    includeComparison,
    previewPending,
    headlineIds,
    summaryResponse,
    comparisonResponse,
  ]);

  const canSave =
    headlineIds.length === HEADLINE_METRIC_COUNT && comparisonWindowValid;

  const buildPack = () => {
    // comparisonRange already reflects (dateFilter, comparisonStart) — an
    // explicit start means it IS the override window.
    const override =
      includeComparison && comparisonStart ? comparisonRange : undefined;
    return {
      dateRange: resolveRange(dateFilter),
      metrics: headlineIds,
      goals: goals.map((goal) => goal.trim()).filter(Boolean),
      lookingAhead: lookingAhead.trim() || undefined,
      useCaseContext: useCaseContext.trim() || undefined,
      includeComparison,
      // Omitted when the AM hasn't overridden it — the backend computes the
      // previous equivalent period itself.
      comparisonDateRange: override
        ? {
            start: toDateString(override.start),
            end: toDateString(override.end),
          }
        : undefined,
    };
  };

  const onSaveInputs = async () => {
    const saved = await sendSaveRequest(buildPack());
    addNotification({
      type: saved ? NotificationType.success : NotificationType.error,
      message: saved
        ? "Report inputs saved."
        : "We couldn't save the report inputs. Please try again.",
      ttl: 5000,
    });
  };

  // Generation saves the current inputs server-side first (same upsert as
  // Save), so the deck always matches the form. Expect ~15-60s.
  const onGenerate = async () => {
    setGenerating(true);
    try {
      const deck = await impactReportService.generate(
        orgCode,
        reportType.id,
        buildPack(),
      );
      // Not auto-opened (ENG-24539): the backend grants the AM edit access on
      // the copy moments before responding, and Drive's grant propagates
      // after a beat — a tab opened on the response lands view-only until
      // reloaded. The notification stays up (no ttl) until they open or
      // dismiss it; the deck is in the history list either way.
      addNotification({
        type: NotificationType.success,
        message: (
          <>
            Impact report generated.{" "}
            <a
              className="impact-report-deck-link"
              href={deck.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open the deck
            </a>
          </>
        ),
      });
    } catch (error) {
      console.error("[Impact Report] - Failed to generate report", error);
      addNotification({
        type: NotificationType.error,
        message:
          error?.data?.detail ??
          "We couldn't generate the report. Please try again.",
        ttl: 8000,
      });
    } finally {
      setGenerating(false);
      // Success prepends the new deck; a failed run also lands in history.
      sendDecksRequest();
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <div className="impact-report-builder">
        <div className="impact-report-builder-content">
          <section className="impact-report-builder-section">
            <div className="impact-report-builder-section-title">
              <span className="impact-report-builder-section-number">1</span>
              <h3>Report type &amp; reporting period</h3>
            </div>
            <div className="impact-report-builder-period">
              <Dropdown
                className="impact-report-builder-report-type"
                selected={reportType}
                onOptionSelected={(option) => setReportType(option)}
              >
                <DropdownButton placeholder="Select report type...">
                  {reportType.name}
                </DropdownButton>
                <DropdownItems>
                  {REPORT_TYPES.map((type) => (
                    <DropdownItem key={type.id} value={type}>
                      {type.name}
                    </DropdownItem>
                  ))}
                </DropdownItems>
              </Dropdown>
              <DateRangeSelector
                dateFilter={dateFilter}
                allTimeStart={allTimeStart}
                onApply={(nextFilter, { onSuccess }) => {
                  setDateFilter(
                    floorAllTime(resolveDateFilter(nextFilter), allTimeStart),
                  );
                  onSuccess();
                }}
              />
            </div>
            <p className="impact-report-builder-hint">
              Changing the period re-pulls outcome data; your inputs are kept.
            </p>
          </section>

          <section className="impact-report-builder-section">
            <div className="impact-report-builder-section-title">
              <span className="impact-report-builder-section-number">2</span>
              <h3>Outcome data &amp; metric selection</h3>
              <Button
                className="impact-report-builder-configure"
                onClick={() => setPickerOpen(true)}
              >
                Configure metrics
              </Button>
            </div>
            <p className="impact-report-builder-hint">
              Pick which metrics lead the report and rank them — the top{" "}
              {HEADLINE_METRIC_COUNT} become the headline outcome tiles. Values
              are pulled and QA&apos;d by the tool; you never edit the numbers.
            </p>
            <ToggleField
              className="impact-report-comparison-toggle"
              label="Compare to previous period"
              value={includeComparison}
              onChange={setIncludeComparison}
              disabled={!comparisonAvailable}
            >
              <span className="impact-report-comparison-hint">
                {comparisonAvailable
                  ? includeComparison
                    ? "Each metric shows its % change vs the window below."
                    : "Off — the deck shows bare counts only."
                  : "Unavailable — the reporting period already starts at this account's first day of data, so there is no earlier period to compare against."}
              </span>
            </ToggleField>
            {includeComparison && comparisonRange && (
              <div className="impact-report-comparison-config">
                <div className="impact-report-comparison-window">
                  <span className="impact-report-comparison-label">
                    Compare from
                  </span>
                  <ImpactReportDatePickerButton
                    date={utcToLocalDate(comparisonRange.start)}
                    minDate={utcToLocalDate(parseDateUtc(allTimeStart))}
                    maxDate={
                      defaultComparisonWindow
                        ? utcToLocalDate(defaultComparisonWindow.start)
                        : undefined
                    }
                    onDateSelected={(date) =>
                      setComparisonStart(
                        date ? localDateToString(date) : undefined,
                      )
                    }
                  />
                  <span className="impact-report-comparison-hint">
                    → {displayComparisonDate(comparisonRange.end)} (end is
                    derived to match the reporting period&apos;s length)
                  </span>
                  {comparisonStart && (
                    <Button
                      size="small"
                      onClick={() => setComparisonStart(undefined)}
                    >
                      Use previous period
                    </Button>
                  )}
                </div>
              </div>
            )}
            <div>
              {includeComparison && comparisonRange && (
                <ul className="impact-report-comparison-changes">
                  {headlineIds.map((id) => {
                    // A failed fetch must not read as a fact about the
                    // account ("no history") — it gets its own label.
                    const chipValue =
                      comparisonError || summaryError
                        ? "unavailable"
                        : (previewChanges[id] ?? "no history");
                    // The chips read left-to-right under the like-ordered
                    // tiles; the label carries the pairing for screen readers.
                    const metricName = metricById(registry, id)?.name;
                    return (
                      <li key={id}>
                        <span
                          className={`impact-report-comparison-chip ${
                            previewChanges[id] &&
                            !comparisonError &&
                            !summaryError
                              ? ""
                              : "empty"
                          }`}
                          aria-label={
                            metricName && !previewPending
                              ? `${metricName} change: ${chipValue}`
                              : undefined
                          }
                        >
                          {previewPending ? <Loader overlay /> : chipValue}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {accessDenied ? (
                <OutcomesEmptyState
                  kind={OutcomesEmptyStateKind.ACCESS_DENIED}
                  level="breakdown"
                />
              ) : (
                <OutcomeTiles metrics={headlineMetrics} tileData={tileData} />
              )}
            </div>
            {pickerOpen && (
              <MetricPickerModal
                seed={draft}
                licensedProducts={ALL_PRODUCTS}
                selectionLimit={HEADLINE_METRIC_COUNT}
                isSaving={false}
                onClose={() => setPickerOpen(false)}
                onSave={({ selectedMetricIds, metricOrder }) => {
                  setDraft({
                    checkedIds: selectedMetricIds,
                    orderedIds: metricOrder,
                  });
                  setPickerOpen(false);
                }}
              />
            )}
          </section>

          <section className="impact-report-builder-section">
            <div className="impact-report-builder-section-title">
              <span className="impact-report-builder-section-number">3</span>
              <h3>Describe the use case</h3>
              <span className="impact-report-builder-optional">Optional</span>
            </div>
            <p className="impact-report-builder-hint">
              How does this partner actually use Transfr? Name the classes and
              programs the headsets are used in, and how they fit into
              instruction. This shapes the report&apos;s narrative and never
              appears word for word.
            </p>
            <Textarea
              label="Use case and program context"
              className="impact-report-deck-goal-textarea"
              placeholder="e.g. Headsets are used in 9th-grade Career Exploration and the Welding I/II CTE pathway; students rotate through 2 sims per week; counselors use career plans in advising."
              value={useCaseContext}
              maxLength={USE_CASE_CONTEXT_MAX_LENGTH}
              onChange={({ target }) => setUseCaseContext(target.value)}
            />
          </section>

          <section className="impact-report-builder-section">
            <div className="impact-report-builder-section-title">
              <span className="impact-report-builder-section-number">4</span>
              <h3>Add goals</h3>
              <span className="impact-report-builder-optional">Optional</span>
            </div>
            <p className="impact-report-builder-hint">
              Describe each program goal in your own words. These appear on the
              account dashboard beside the outcome data — add any that help, or
              skip this step entirely.
            </p>
            {goals.map((goal, index) => (
              <Textarea
                key={`goal-${index}`}
                label={`Goal ${index + 1} language`}
                className="impact-report-deck-goal-textarea"
                placeholder={GOAL_PLACEHOLDERS[index]}
                value={goal}
                onChange={({ target }) =>
                  setGoals((current) =>
                    current.map((g, i) => (i === index ? target.value : g)),
                  )
                }
              />
            ))}
          </section>

          <section className="impact-report-builder-section">
            <div className="impact-report-builder-section-title">
              <span className="impact-report-builder-section-number">5</span>
              <h3>Looking ahead</h3>
              <span className="impact-report-builder-optional">Optional</span>
            </div>
            <p className="impact-report-builder-hint">
              Where is this partnership going next? Rough bullets are fine —
              they&apos;re polished into the report&apos;s closing section.
            </p>
            <Textarea
              label="Looking ahead notes"
              className="impact-report-deck-goal-textarea"
              placeholder="e.g. Expand into the CNA pathway — insert Transfr as a program prerequisite; grow CTE alignment; continue career exploration integration."
              value={lookingAhead}
              maxLength={LOOKING_AHEAD_MAX_LENGTH}
              onChange={({ target }) => setLookingAhead(target.value)}
            />
          </section>
        </div>
        <div className="impact-report-builder-card">
          {promptConfigEnabled && (
            <>
              <button
                type="button"
                className="impact-report-prompt-config-button"
                title="Prompt config (dev)"
                aria-label="Prompt config (dev)"
                onClick={() => setPromptConfigOpen(true)}
              >
                <FontAwesomeIcon icon="fa-regular fa-sliders" />
              </button>
              <ImpactReportPromptConfigModal
                open={promptConfigOpen}
                onClose={() => setPromptConfigOpen(false)}
                reportType={reportType.id}
              />
            </>
          )}
          {decks.length > 0 ? (
            <>
              <h2>
                Past decks
                <span className="impact-report-deck-count">{decks.length}</span>
              </h2>
              <ul className="impact-report-deck-list">
                {decks.map((deck) => (
                  <DeckHistoryRow
                    key={deck.id}
                    deck={deck}
                    typeName={
                      REPORT_TYPES.find((type) => type.id === deck.reportType)
                        ?.name ?? deck.reportType
                    }
                  />
                ))}
              </ul>
            </>
          ) : (
            <>
              <h2>Impact Report Builder</h2>
              <p>
                No report yet · save inputs and generate a 2-page core draft
              </p>
            </>
          )}
        </div>
      </div>
      <div className="impact-report-builder-footer">
        {!canSave && (
          <span className="impact-report-builder-hint">
            Select exactly {HEADLINE_METRIC_COUNT} metrics to save.
          </span>
        )}
        <Button
          disabled={!canSave || saving || generating}
          loader={saving}
          onClick={onSaveInputs}
        >
          Save inputs
        </Button>
        <Button
          className="impact-report-builder-generate"
          primary
          disabled={!canSave || saving || generating}
          loader={generating}
          onClick={onGenerate}
        >
          Generate Impact Report
        </Button>
      </div>
    </>
  );
}

export function ImpactReportTab({ organization, childOrgs }) {
  return {
    name: "reportBuilder",
    header: (
      <TabHeader name="reportBuilder" className="impact-report-tab-header">
        <div className="tab-header-text">Report Builder</div>
        <span className="impact-report-beta-badge">BETA</span>
      </TabHeader>
    ),
    content: (
      <Tab name="reportBuilder" className="impact-report-tab-panel">
        {organization?.code && (
          <ImpactReportMetricRegistry>
            <ImpactReportBuilder
              organization={organization}
              childOrgs={childOrgs}
            />
          </ImpactReportMetricRegistry>
        )}
      </Tab>
    ),
  };
}
